/**
 * The weekly security scan on a Saidiseire site page: the CRM's side of the
 * contract, and the rules the card needs (docs/saidiseire-kiirus-turve-raport.md,
 * item B).
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/crm-monitoring/security/types.ts`.
 * `monitoringSecurity.test.ts` reads that file and compares the field names of
 * every interface, holds the finding codes and coverage notes to the API's
 * lists (`security/snapshot.ts`) and the polling numbers to `RUN_MAX_MS` and
 * `RUN_POLL_MS` in `periodic/claim.ts`.
 *
 * React-free: which state the card is in, which tone a verdict wears, which
 * sentence a finding gets and when polling gives up are decided here and
 * tested without mounting anything.
 */
import { api } from "./api";
import { MONITOR_SITES_PATH } from "./monitoring";

// ── Contract (mirrors crm-monitoring/security/types.ts) ─────────────────────────

export type SecurityVerdict = "ok" | "warning" | "critical" | "incomplete";

export type SecurityLevel = "critical" | "warning" | "info";

export type SecurityInventorySource = "reporter" | "page" | "none";

export interface SecurityFindingView {
  key: string;
  code: string;
  subject: string | null;
  level: SecurityLevel;
  count: number | null;
  detail: string | null;
  /** The subject's name when the scan knew one (a closed plugin's); absent on older scans. */
  name?: string | null;
}

export interface SecurityVulnView {
  key: string;
  /** The Wordfence record id. */
  id: string;
  type: string;
  slug: string;
  name: string;
  /** The installed version the match was made on. */
  version: string;
  title: string;
  cvssScore: number | null;
  cvssRating: string | null;
  cve: string | null;
  /** The Wordfence record, https only (the licence requires the link). */
  link: string | null;
  cveLink: string | null;
  fixedIn: string | null;
  /** The version to update this software to, the same on all its records; null when none clears them all (or an older scan). */
  fixAll: string | null;
  level: SecurityLevel;
  /** Matched on a version the homepage shows, not the reporter's exact one. */
  possible: boolean;
}

export interface SecurityAttributionView {
  source: string;
  notice: string;
  license: string;
  licenseUrl: string | null;
}

export interface SecurityInventoryView {
  source: SecurityInventorySource;
  wordpress: boolean;
  core: string | null;
  php: string | null;
  plugins: number;
  themes: number;
  reportAt: string | null;
}

export interface SecurityDiffView {
  since: string;
  added: SecurityFindingView[];
  resolved: SecurityFindingView[];
}

export interface SecurityScanView {
  id: string;
  at: string;
  verdict: SecurityVerdict;
  inventory: SecurityInventoryView;
  findings: SecurityFindingView[];
  vulns: SecurityVulnView[];
  notes: string[];
  attributions: SecurityAttributionView[];
  diff: SecurityDiffView | null;
}

export interface SecurityHistoryRow {
  at: string;
  verdict: SecurityVerdict;
  critical: number;
  warnings: number;
  vulns: number;
}

export interface SecurityFeedView {
  keySet: boolean;
  fetchedAt: string | null;
  entries: number;
  stale: boolean;
  /** Why the last download failed (`settings.wordfence.feedErrors`), or null. */
  error: string | null;
}

export interface SecurityErrorView {
  at: string;
  error: string;
}

export interface SecurityCardResponse {
  allowed: boolean;
  feed: SecurityFeedView;
  running: boolean;
  latest: SecurityScanView | null;
  history: SecurityHistoryRow[];
  lastError: SecurityErrorView | null;
  nextDueAt: string | null;
}

export interface SecurityRunResponse {
  startedAt: string;
}

// ── Requests ───────────────────────────────────────────────────────────────────

export const securityPath = (monitorId: string) => `${MONITOR_SITES_PATH}/${encodeURIComponent(monitorId)}/security`;

export const getSecurity = (monitorId: string, signal?: AbortSignal): Promise<SecurityCardResponse> =>
  api.get<SecurityCardResponse>(securityPath(monitorId), signal);

/** Start a scan now; refusals come back as an `ApiError` (worded by `monitorErrorKey`). */
export const runSecurityNow = (monitorId: string): Promise<SecurityRunResponse> =>
  api.post<SecurityRunResponse>(`${securityPath(monitorId)}/run`);

// ── Rules ────────────────────────────────────────────────────────────────────

/** How often the card asks while a scan is going on (`RUN_POLL_MS` in periodic/claim.ts). */
export const SECURITY_POLL_MS = 5_000;
/** The longest one scan may take (`RUN_MAX_MS.security` in periodic/claim.ts). */
export const SECURITY_RUN_MAX_MS = 180_000;
/** How long the card keeps asking after it first saw a scan going on. */
export const SECURITY_POLL_LIMIT_MS = SECURITY_RUN_MAX_MS + 30_000;

/** Whether to ask again: not while nothing runs; give up once the window is over. */
export function securityPollStep(running: boolean, since: number | null, now: number): "idle" | "poll" | "gaveUp" {
  if (!running) return "idle";
  if (since !== null && now - since >= SECURITY_POLL_LIMIT_MS) return "gaveUp";
  return "poll";
}

/**
 * What a FRESH answer does to the poll (`pollAfterAnswer` in monitoringSpeed,
 * on the scan's window): only an answer read just now may give up, so a tab
 * hidden past the window reads once more before it says "still running".
 */
export function securityPollAfterAnswer(running: boolean, since: number | null, now: number): { since: number | null; gaveUp: boolean } {
  if (!running) return { since: null, gaveUp: false };
  if (since === null) return { since: now, gaveUp: false };
  return { since, gaveUp: securityPollStep(true, since, now) === "gaveUp" };
}

/**
 * What the card leads with. `notInPlan` and `noKey` come first because a scan
 * cannot run then; `noFeed` (a key but no feed download yet) waits for the
 * nightly feed, or says why its download fails (`feed.error`: a refused key
 * never gets one); `never` is a site the weekly round has not reached.
 */
export type SecurityCardState = "notInPlan" | "noKey" | "noFeed" | "never" | "scanned";

export function securityCardState(data: Pick<SecurityCardResponse, "allowed" | "feed" | "latest">): SecurityCardState {
  if (!data.allowed) return "notInPlan";
  if (!data.feed.keySet) return "noKey";
  if (!data.feed.fetchedAt && !data.latest) return "noFeed";
  return data.latest ? "scanned" : "never";
}

/** Whether the "check now" button is offered (the server still refuses anything it must). */
export function canScanNow(data: Pick<SecurityCardResponse, "allowed" | "feed" | "running">): boolean {
  return data.allowed && data.feed.keySet && !data.running;
}

export function verdictTone(verdict: SecurityVerdict): "ok" | "wt" | "lt" | "mut" {
  if (verdict === "ok") return "ok";
  if (verdict === "warning") return "wt";
  if (verdict === "critical") return "lt";
  return "mut";
}

export function levelTone(level: SecurityLevel): "lt" | "wt" | "info" {
  if (level === "critical") return "lt";
  if (level === "warning") return "wt";
  return "info";
}

/** Every finding code the API can raise (`SECURITY_FINDING_CODES` in security/snapshot.ts). */
export const SECURITY_FINDING_CODES = [
  "vuln",
  "core_insecure",
  "core_outdated",
  "plugin_closed",
  "plugins_outdated",
  "plugins_abandoned",
  "theme_outdated",
  "php_eol",
  "exposed_file",
  "malware",
  "blocklisted",
  "wp_core_modified",
  "wp_core_unexpected",
  "wp_uploads_php",
  "wp_new_admins",
  "wp_exposed_files",
] as const;

/** Every coverage note the API can write (`SECURITY_NOTES` in security/snapshot.ts). */
export const SECURITY_NOTES = [
  "homepage_failed",
  "homepage_walled",
  "no_reporter",
  "reporter_stale",
  "not_wordpress",
  "on_platform",
  "feed_missing",
  "feed_stale",
  "reputation_unchecked",
] as const;

/** The malware signs the scanner knows, as keys under `monitoring.security.malware`. */
const MALWARE_SIGNS: Record<string, string> = {
  "malware-injected-loader": "injectedLoader",
  "malware-defaced": "defaced",
  "malware-cloaked-content": "cloakedContent",
  "malware-seo-spam": "seoSpam",
  "malware-hidden-links": "hiddenLinks",
  "malware-foreign-title": "foreignTitle",
};
export const MALWARE_SIGN_KEYS = [...Object.values(MALWARE_SIGNS), "other"];

/**
 * wordpress.org's closure reasons (the plugin directory's own list), each with
 * a sentence of its own under `monitoring.security.finding`. A reason not on
 * the list, or none, reads as a plain closure: the raw reason is an English
 * slug and is never printed.
 */
const CLOSED_REASONS: Record<string, string> = {
  "security-issue": "plugin_closed_security",
  "author-request": "plugin_closed_author",
  "guideline-violation": "plugin_closed_guidelines",
  "licensing-trademark-violation": "plugin_closed_licensing",
  "merged-into-core": "plugin_closed_merged",
  unused: "plugin_closed_unused",
};
export const CLOSED_REASON_KEYS = Object.values(CLOSED_REASONS);

/** A catalogue key and the values its sentence prints. */
export interface SecurityMessage {
  key: string;
  vars: Record<string, string | number>;
}

const P = "monitoring.security";

/**
 * The sentence of one finding. Every sentence can print `{n}` (the count),
 * `{detail}` and `{subject}`; a closed plugin has a sentence per closure
 * reason and is named by its name when the scan knew it (the slug otherwise),
 * and a malware sign has a sentence of its own. A vulnerability is worded by
 * `newsMessage`, which has the scan's list to name it from.
 */
export function findingMessage(f: SecurityFindingView): SecurityMessage {
  const vars = { n: f.count ?? 0, detail: f.detail ?? "", subject: f.subject ?? "" };
  if (f.code === "plugin_closed") {
    return { key: `${P}.finding.${CLOSED_REASONS[f.detail ?? ""] ?? "plugin_closed"}`, vars: { ...vars, subject: f.name || f.subject || "" } };
  }
  if (f.code === "malware") return { key: `${P}.malware.${MALWARE_SIGNS[f.subject ?? ""] ?? "other"}`, vars };
  return { key: `${P}.finding.${f.code}`, vars };
}

/**
 * "CVSS 9.8, kriitiline", "CVSS 5.0", "keskmine", or null: a vulnerability's
 * severity mid-sentence. `word` gives the rating in the reader's language
 * (lower case, it sits inside a sentence).
 */
export function vulnSeverity(v: Pick<SecurityVulnView, "cvssScore" | "cvssRating">, word: (rating: CvssRating) => string): string | null {
  const rating = cvssRating(v.cvssRating);
  const parts = [v.cvssScore !== null ? `CVSS ${v.cvssScore.toFixed(1)}` : null, rating ? word(rating) : null].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

/**
 * A "what is new" line. A vulnerability is named in our own words from the
 * scan's list: the software (the site's own name for it, else the feed's, else
 * the slug), its version and its severity, never the feed record's title (the
 * table below carries that, with its link and licence). Anything else is
 * `findingMessage`.
 */
export function newsMessage(f: SecurityFindingView, vulns: readonly SecurityVulnView[], word: (rating: CvssRating) => string): SecurityMessage {
  if (f.code !== "vuln") return findingMessage(f);
  const v = vulns.find((x) => x.key === f.key);
  // Not in the list (it always is, the two come from one scan): the slug from the subject, `type:slug:id`.
  const [type, slug] = (f.subject ?? "").split(":");
  const software = v ? `${vulnSoftware(v)} ${v.version}` : type === "core" ? "WordPress" : slug || (f.subject ?? "");
  const severity = v ? vulnSeverity(v, word) : null;
  return severity ? { key: `${P}.finding.vuln`, vars: { software, severity } } : { key: `${P}.finding.vulnPlain`, vars: { software } };
}

/**
 * A history row's counts as words: "2 kriitilist, 5 hoiatust, 1 turvaauk",
 * zeros left out, and "no problems" for a row with none (an incomplete scan's
 * zeros say nothing, its pill says why). The vulnerabilities are counted on
 * their own: a Low rated one is neither critical nor a warning, so they are
 * not "of which".
 */
export function historyCounts(row: Pick<SecurityHistoryRow, "verdict" | "critical" | "warnings" | "vulns">): SecurityMessage[] {
  const parts: SecurityMessage[] = [];
  if (row.critical > 0) parts.push({ key: `${P}.historyCount.critical`, vars: { n: row.critical } });
  if (row.warnings > 0) parts.push({ key: `${P}.historyCount.warnings`, vars: { n: row.warnings } });
  if (row.vulns > 0) parts.push({ key: `${P}.historyCount.vulns`, vars: { n: row.vulns } });
  if (parts.length === 0 && row.verdict !== "incomplete") parts.push({ key: `${P}.historyCount.none`, vars: {} });
  return parts;
}

/** A coverage note's sentence key; an unknown note (a newer server) falls back to a generic one. */
export function noteKey(note: string): string {
  return (SECURITY_NOTES as readonly string[]).includes(note) ? `${P}.note.${note}` : `${P}.note.other`;
}

/** The software a vulnerability is in, as the card names it. */
export function vulnSoftware(v: Pick<SecurityVulnView, "type" | "name" | "slug">): string {
  return v.type === "core" ? "WordPress" : v.name || v.slug;
}

/** CVSS's severity ratings, as keys under `monitoring.security.cvss`. */
export const CVSS_RATINGS = ["none", "low", "medium", "high", "critical"] as const;
export type CvssRating = (typeof CVSS_RATINGS)[number];

/** The feed's rating ("None" .. "Critical") as a catalogue key; null for one this list does not know. */
export function cvssRating(raw: string | null): CvssRating | null {
  const key = raw?.trim().toLowerCase() ?? "";
  return (CVSS_RATINGS as readonly string[]).includes(key) ? (key as CvssRating) : null;
}

/**
 * "9.8 (Kriitiline)", "Keskmine", or null when the record has neither. The
 * rating is the feed's English word, so it is shown in the reader's language
 * through `word` (`monitoring.security.cvss.<rating>`).
 */
export function cvssText(v: Pick<SecurityVulnView, "cvssScore" | "cvssRating">, word: (rating: CvssRating) => string): string | null {
  const rating = cvssRating(v.cvssRating);
  const label = rating ? word(rating) : null;
  if (v.cvssScore !== null && label) return `${v.cvssScore.toFixed(1)} (${label})`;
  if (v.cvssScore !== null) return v.cvssScore.toFixed(1);
  return label;
}

/** The feed's source as the footer names it. */
export function attributionSource(source: string): "defiant" | "mitre" | "other" {
  return source === "defiant" || source === "mitre" ? source : "other";
}

/** Wordfence's own terms for the Intelligence feed, which the footer links to. */
export const WORDFENCE_TERMS_URL = "https://www.wordfence.com/wordfence-intelligence-terms-and-conditions/";

/** Where the Wordfence key is set (Settings > Integrations > Wordfence). */
export const WORDFENCE_SETTINGS_PATH = "/settings/integrations/wordfence";
